import { useEffect, useState } from 'react';
import { produce } from 'immer';

import { OrganizationPreset } from '@paragon/contract-library';

import {
  getAllSettingsTypesSupportingPresets,
  SettingsOfType,
  settingsPresetsOfType,
  SettingsPresetType,
} from '../presets/presetTypes';

export type DataWithStatus<T> = 'fetching' | 'error' | T;

type PresetPropertyName<K extends SettingsPresetType> = `${K}Presets`;

type FetchedPresets = {
  [K in SettingsPresetType as PresetPropertyName<K>]: DataWithStatus<OrganizationPreset<SettingsOfType<K>>[]>;
};

interface Options {
  suppressFetchingState?: boolean;
}

interface Result extends FetchedPresets {
  invalidate: (types: SettingsPresetType[] | 'all', options?: Options) => Promise<void>;
  getPresetsOfType: <K extends SettingsPresetType>(type: K) => DataWithStatus<OrganizationPreset<SettingsOfType<K>>[]>;
}

interface ResultForType<T extends SettingsPresetType> {
  invalidate: (options?: Options) => Promise<void>;
  presets: DataWithStatus<OrganizationPreset<SettingsOfType<T>>[]>;
}

export function useFetchedPresetsOfSingleType<T extends SettingsPresetType>(
  type: T,
  options?: Options,
): ResultForType<T> {
  const { getPresetsOfType, invalidate: invalidateType } = useFetchedPresets([type], options);

  const presets = getPresetsOfType(type);
  const invalidate = (options?: Options) => invalidateType([type], options);

  return { presets, invalidate };
}

export function useFetchedPresets(requestedTypes: SettingsPresetType[] | 'all', options?: Options): Result {
  const typesToFetch = requestedTypes === 'all' ? getAllSettingsTypesSupportingPresets() : requestedTypes;

  const [presetsMap, setPresetsMap] = useState<{
    [K in SettingsPresetType]?: DataWithStatus<OrganizationPreset<SettingsOfType<K>>[]>;
  }>(() => Object.fromEntries(typesToFetch.map((type) => [type, 'fetching'] as const)));

  async function fetchPresets(typesToFetch: SettingsPresetType[], fetchOptions?: Options) {
    if (fetchOptions?.suppressFetchingState !== true) {
      setPresetsMap(produce((map) => typesToFetch.forEach((type) => (map[type] = 'fetching'))));
    }

    await Promise.all(
      typesToFetch.map(async (type) =>
        settingsPresetsOfType(type)
          .fetch()
          .then((presets) => setPresetsMap((prev) => ({ ...prev, [type]: presets })))
          .catch(() => setPresetsMap((prev) => ({ ...prev, [type]: 'error' }))),
      ),
    );
  }

  useEffect(() => {
    void fetchPresets(typesToFetch, options);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchedPresets = Object.fromEntries(
    getAllSettingsTypesSupportingPresets().map((type) => [`${type}Presets`, presetsMap[type] ?? []]),
  ) as FetchedPresets;

  function getPresetsOfType<K extends SettingsPresetType>(
    type: K,
  ): DataWithStatus<OrganizationPreset<SettingsOfType<K>>[]> {
    return presetsMap[type] ?? [];
  }

  function invalidate(typesToInvalidate: SettingsPresetType[] | 'all', invalidateOptions?: Options) {
    return fetchPresets(typesToInvalidate === 'all' ? typesToFetch : typesToInvalidate, invalidateOptions);
  }

  return { ...fetchedPresets, getPresetsOfType, invalidate };
}
